import type { Link } from '@unhead/vue'

export function useHrefLang(): void {
  const route = useRoute()
  const i18n = useI18n()
  const config = useRuntimeConfig()
  const requestUrl = useRequestURL()

  const BASEURL = (config.public.host ? `https://${config.public.host}` : requestUrl.origin).toString()
  const KEY_CHANGE: Record<string, string> = { cz: 'cs', dk: 'da', se: 'sv' }

  const { metaObject, updateMeta } = useLocaleHead({
    addDirAttribute: true,
    addSeoAttributes: true,
    baseUrl: BASEURL,
    identifierAttribute: 'id',
  })

  const hrefLangDefault = computed(() => {
    const locales = i18n.$getLocales().map((l) => l.code)
    const localeRegex = new RegExp(`^/(${locales.join('|')})(?=/|$)`)
    const normalizedPath = route.path.replace(localeRegex, '') || '/'

    return {
      href: `${BASEURL}${normalizedPath}`,
      hreflang: 'x-default',
      rel: 'alternate' as const,
    }
  })

  const hrefLangMeta = computed(() =>
    metaObject.value.meta.map((meta) => {
      const metaWithoutId = { ...meta }
      delete metaWithoutId.id

      if (
        (metaWithoutId.property === 'og:locale' || metaWithoutId.property === 'og:locale:alternate') &&
        metaWithoutId.content
      ) {
        metaWithoutId.content = KEY_CHANGE[metaWithoutId.content] ?? metaWithoutId.content
      }

      return metaWithoutId
    }),
  )

  const hrefLangLinks = computed<Link[]>(() =>
    metaObject.value.link.map((link) => {
      const linkWithoutId = { ...link }
      delete linkWithoutId.id

      if (link.rel !== 'alternate' || !link.hreflang) {
        return linkWithoutId as Link
      }

      return {
        ...linkWithoutId,
        hreflang: KEY_CHANGE[link.hreflang] ?? link.hreflang,
      } as Link
    }),
  )

  useHead(() => ({
    htmlAttrs: metaObject.value.htmlAttrs,
    link: [...hrefLangLinks.value, hrefLangDefault.value],
    meta: hrefLangMeta.value,
  }))

  if (import.meta.server) {
    updateMeta()
    return
  }

  watch(
    () => route.fullPath,
    () => updateMeta(),
    { immediate: true },
  )
}
